<script setup lang="ts">
import type { Component } from 'vue';
import { useLoyaltyHubStore } from '@/stores/loyalty-hub';
import { getAssetsUrlForCDN } from '@/utils/template';

// Comemoração ao ATIVAR as notificações de marketing (ENG-491). O feedback
// antes era só o toaster; aqui parabeniza e aproveita o momento pra empurrar
// o Clube Fidelidade (roleta, check-in, códigos...). Mesmo casco do
// LogoutConfirmModal (sheet no mobile, card no desktop). Abre a partir de
// useMarketingOptIn (ref de módulo), montado uma vez no Shell.vue.
const isOpen = defineModel<boolean>({ required: true });

// pelo CDN estático (appStaticCdn), como os outros assets do painel
const ART = getAssetsUrlForCDN('/assets/images/user-v2/optin-celebration.webp');

useBodyScrollLock(isOpen);

const { isClubFidelidadeEnabled } = useFeatureFlagsJson();
const loyaltyHub = useLoyaltyHubStore();

// ── Comemoração (Lottie) ─────────────────────────────────────────────────────
// Mesmo confete e mesmo esquema do RoulettePrizePopup.vue: player + animação
// por import dinâmico (lottie-web ~250kB fora do chunk do painel), estoura
// quando o modal abre e desmonta em CELEBRATION_MS — o fade do CSS termina
// junto, então o último frame nunca fica congelado.
const lottiePlayer = shallowRef<Component | null>(null);
const celebrationData = shallowRef<object | null>(null);
const CELEBRATION_SPEED = 0.8;
const CELEBRATION_MS = 2100;
const celebrating = ref(false);
let celebrateTimer: ReturnType<typeof setTimeout> | null = null;

function stopCelebration() {
  if (celebrateTimer) {
    clearTimeout(celebrateTimer);
    celebrateTimer = null;
  }

  celebrating.value = false;
}

async function celebrate() {
  if (!lottiePlayer.value || !celebrationData.value) return;

  stopCelebration();
  await nextTick();

  celebrating.value = true;
  celebrateTimer = setTimeout(stopCelebration, CELEBRATION_MS);
}

onMounted(async () => {
  try {
    const [player, data] = await Promise.all([
      import('vue3-lottie'),
      import('@/assets/json/lottie/success_celebration.json'),
    ]);

    lottiePlayer.value = player.Vue3Lottie;
    celebrationData.value = data.default;
  } catch {
    // decorativo: sem o chunk o modal abre igual
  }
});

onBeforeUnmount(stopCelebration);

// o watch do player cobre o chunk chegar depois da abertura (raro)
watch([isOpen, lottiePlayer], ([opened]) => {
  if (opened) celebrate();
  else stopCelebration();
});

function close() {
  isOpen.value = false;
}

// O hub é OUTRO overlay (z-9990) por cima do painel — não fecha o Minha Conta,
// igual aos atalhos /?open= do ScreenProfile.vue. Só fecha esta comemoração.
function openLoyaltyHub() {
  close();
  loyaltyHub.open();
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="user-v2-marketing-optin-celebration-modal fixed inset-0 z-[100] flex items-end justify-center font-sans lg:items-center lg:p-[16px]"
      @dragstart.prevent
    >
      <div
        class="modal-backdrop absolute inset-0 bg-black/70"
        @click="close"
      />

      <div
        class="modal-card relative flex w-full flex-col gap-[20px] rounded-t-[20px] bg-v2-background px-[12px] pb-[40px] pt-[16px] text-center lg:max-w-[420px] lg:rounded-[20px] lg:pb-[24px]"
      >
        <!-- Luz verde no topo, mesmo princípio do degradê do modal de
             desativar (--v2-danger lá, --v2-success aqui): borda a borda,
             apagada em ~28% da altura, reforço radial atrás da arte. -->
        <div
          aria-hidden="true"
          class="modal-glow pointer-events-none absolute inset-0 rounded-t-[20px] lg:rounded-[20px]"
        />

        <UserV2ModalCloseButton @close="close" />

        <!-- Arte do Figma (node 18657:35897 "ICON IMAGE": cofre + calendário +
             cronômetro + confetes). Exportada do raw com alpha (o export do
             node assa fundo #444), recortada na bbox e salva em webp 1200w.
             Sangra acima do card, como as folhas da roletinha. -->
        <img
          alt=""
          class="relative mx-auto mt-[-56px] w-[82%] max-w-[320px] select-none"
          draggable="false"
          :src="ART"
        />

        <div class="relative flex flex-col gap-[8px]">
          <p class="text-[18px] font-bold leading-[28px] text-white">
            Notificações ativadas!
          </p>
          <p class="text-[14px] leading-[20px] text-v2-muted">
            Agora você recebe ofertas, giros grátis, promoções e novidades em
            primeira mão.
          </p>
        </div>

        <div
          v-if="isClubFidelidadeEnabled"
          class="relative flex flex-col gap-[4px] rounded-[12px] bg-v2-success-50 p-[12px]"
        >
          <p class="text-[14px] font-bold leading-[20px] text-v2-foreground">
            Aproveite e conheça o Clube Fidelidade
          </p>
          <p class="text-[12px] leading-[16px] text-v2-muted">
            Roleta diária, check-in, códigos, cashback e prêmios — tudo em um só
            lugar.
          </p>
        </div>

        <!-- Botões no padrão do modal de desativar (Figma 23655:89554): 40px,
             mín. 124px, raio 12, bold 14, centralizados; cinza = ficar, verde =
             ação principal. -->
        <div class="relative flex justify-center gap-[10px]">
          <button
            class="modal-btn flex h-[40px] flex-1 items-center justify-center whitespace-nowrap rounded-[12px] px-[8px] text-[13px] font-bold text-white"
            :class="isClubFidelidadeEnabled ? 'bg-v2-default' : 'bg-v2-success'"
            type="button"
            @click="close"
          >
            Continuar no perfil
          </button>
          <button
            v-if="isClubFidelidadeEnabled"
            class="modal-btn flex h-[40px] flex-1 items-center justify-center whitespace-nowrap rounded-[12px] bg-v2-success px-[8px] text-[13px] font-bold text-white"
            type="button"
            @click="openLoyaltyHub"
          >
            Acessar o Clube
          </button>
        </div>

        <!-- confete: último filho, pinta por cima de tudo; pointer-events-none
             mantém os botões clicáveis durante a animação -->
        <div
          v-if="celebrating"
          class="celebrate"
          :style="{ '--celebrate-ms': `${CELEBRATION_MS}ms` }"
        >
          <component
            :is="lottiePlayer"
            :animation-data="celebrationData"
            :auto-play="true"
            class="celebrate__anim"
            :loop="false"
            :speed="CELEBRATION_SPEED"
          />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
.modal-btn {
  // halo escuro atrás do rótulo: contraste do branco sobre o verde/cinza
  text-shadow:
    0 1px 2px rgba(0, 7, 23, 0.6),
    0 0 6px rgba(0, 7, 23, 0.35);
}

.modal-glow {
  z-index: 0;
  background:
    radial-gradient(
      ellipse 60% 30% at 50% 4%,
      rgb(var(--v2-success) / 0.45) 0%,
      rgb(var(--v2-success) / 0) 100%
    ),
    linear-gradient(
      180deg,
      rgb(var(--v2-success) / 0.95) 0%,
      rgb(18 160 82 / 0.9) 8%,
      rgb(8 90 48 / 0.6) 18%,
      rgb(0 7 23 / 0) 28%
    );
}

// main.scss reseta `transition` — entrada via @keyframes (mesmo esquema dos
// outros modais do painel V2).
.celebrate {
  @apply pointer-events-none absolute select-none;

  z-index: 3;
  inset: -22%;
  // um ciclo só (entrada + saída) com a duração do timer que desmonta
  animation: optin-celebrate-life var(--celebrate-ms, 2100ms) linear forwards;
}

.celebrate__anim {
  @apply absolute inset-0;
}

@keyframes optin-celebrate-life {
  0% {
    opacity: 0;
  }

  6% {
    opacity: 1;
  }

  60% {
    opacity: 1;
    animation-timing-function: ease-in;
  }

  100% {
    opacity: 0;
  }
}

.modal-backdrop {
  animation: marketing-optin-fade 0.18s ease-out;
}

.modal-card {
  animation: marketing-optin-pop 0.2s ease-out;

  @media (max-width: 1023px) {
    animation-name: marketing-optin-slide-up;
  }
}

@keyframes marketing-optin-fade {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes marketing-optin-pop {
  from {
    opacity: 0;
    transform: translateY(12px) scale(0.96);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes marketing-optin-slide-up {
  from {
    opacity: 0;
    transform: translateY(100%);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
